import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Plus, Trash2, CalendarRange, ClipboardCheck, Wand2, AlertTriangle } from 'lucide-react';

const addDays = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// Spread `n` due dates evenly across [start, end] (last one lands on `end`).
export const spreadDates = (start, end, n) => {
  if (!start || !end || n <= 0) return Array.from({ length: n }, () => '');
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const span = Math.max(e - s, 0);
  return Array.from({ length: n }, (_, i) => new Date(s + (span * (i + 1)) / n).toISOString().slice(0, 10));
};

export const newPhase = (index, prev) => {
  const start = prev?.end_date ? addDays(prev.end_date, 1) : '';
  const end = start ? addDays(start, 120) : '';
  return { id: undefined, name: `Phase ${index + 1}`, start_date: start, end_date: end, num_assessments: 2, assessment_due_dates: spreadDates(start, end, 2) };
};

/**
 * Editable list of cycle phases. Each phase has its own window and a fixed
 * number of assessments with hand-picked due dates.
 */
export const PhasesEditor = ({ phases, onChange, disabled = false, testIdPrefix = 'phase' }) => {
  const update = (i, patch) => onChange(phases.map((p, idx) => {
    if (idx !== i) return p;
    const next = { ...p, ...patch };
    if (('start_date' in patch || 'end_date' in patch) && next.start_date && next.end_date) {
      const spread = spreadDates(next.start_date, next.end_date, (next.assessment_due_dates || []).length);
      next.assessment_due_dates = (next.assessment_due_dates || []).map((d, j) => d || spread[j]);
    }
    return next;
  }));

  const setCount = (i, raw) => {
    const n = Math.max(0, Math.min(20, Number(raw) || 0));
    const p = phases[i];
    const cur = p.assessment_due_dates || [];
    const next = n <= cur.length ? cur.slice(0, n) : spreadDates(p.start_date, p.end_date, n);
    update(i, { num_assessments: n, assessment_due_dates: next });
  };

  const setDue = (i, j, v) => {
    const next = [...(phases[i].assessment_due_dates || [])];
    next[j] = v;
    update(i, { assessment_due_dates: next });
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-editor`}>
      {phases.length === 0 && (
        <p className="text-xs text-gray-400 italic">No phases yet. Add the first phase to split this cycle.</p>
      )}
      {phases.map((p, i) => (
        <div key={p.id || i} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-3" data-testid={`${testIdPrefix}-${i}`}>
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 shrink-0 rounded-full bg-ica-navy text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
            <Input
              data-testid={`${testIdPrefix}-${i}-name`}
              value={p.name}
              disabled={disabled}
              onChange={(e) => update(i, { name: e.target.value })}
              className="h-8 text-sm font-medium flex-1"
              placeholder="Phase name (e.g. Year 1 · Semester 2)"
            />
            {!disabled && (
              <Button size="sm" variant="ghost" data-testid={`${testIdPrefix}-${i}-remove`} onClick={() => onChange(phases.filter((_, idx) => idx !== i))} className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px]">Start</Label>
              <Input data-testid={`${testIdPrefix}-${i}-start`} type="date" value={p.start_date} disabled={disabled} className="h-8 text-xs"
                onChange={(e) => update(i, { start_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px]">End</Label>
              <Input data-testid={`${testIdPrefix}-${i}-end`} type="date" value={p.end_date} disabled={disabled} className="h-8 text-xs"
                onChange={(e) => update(i, { end_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px]">Number of assessments</Label>
              <Input data-testid={`${testIdPrefix}-${i}-count`} type="number" min={0} max={20} value={p.num_assessments} disabled={disabled} className="h-8 text-xs"
                onChange={(e) => setCount(i, e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 -mt-2">
            <p className="col-span-3 text-[10px] text-gray-500">Each assessment gets its own due date below.</p>
          </div>
          {(p.assessment_due_dates || []).length > 0 ? (
            <div className="rounded-md border border-dashed border-ica-navy/30 bg-white p-3" data-testid={`${testIdPrefix}-${i}-due-dates`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-ica-navy whitespace-nowrap">
                    <ClipboardCheck className="w-3.5 h-3.5" /> Assessment due dates
                  </div>
                  <p className="text-[10px] text-gray-500">When the chef must submit each assessment for this phase.</p>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    data-testid={`${testIdPrefix}-${i}-spread`}
                    onClick={() => update(i, { assessment_due_dates: spreadDates(p.start_date, p.end_date, p.assessment_due_dates.length) })}
                    className="text-[11px] text-ica-navy hover:underline inline-flex items-center gap-1 whitespace-nowrap shrink-0"
                  >
                    <Wand2 className="w-3 h-3" /> Spread evenly across phase
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {p.assessment_due_dates.map((d, j) => {
                  const outside = d && ((p.start_date && d < p.start_date) || (p.end_date && d > p.end_date));
                  return (
                    <div key={j} className={`rounded-md border px-2.5 py-2 ${outside ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50/60'}`}>
                      <Label className="text-[11px] text-gray-600">Assessment {j + 1} due</Label>
                      <Input data-testid={`${testIdPrefix}-${i}-due-${j}`} type="date" value={d} disabled={disabled} className="h-8 text-xs mt-1 bg-white"
                        min={p.start_date || undefined} max={p.end_date || undefined}
                        onChange={(e) => setDue(i, j, e.target.value)} />
                      {outside && (
                        <p data-testid={`${testIdPrefix}-${i}-due-${j}-warning`} className="text-[10px] text-amber-700 mt-1 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Outside this phase ({p.start_date} → {p.end_date})
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 italic">No assessments in this phase — set a number above to add due dates.</p>
          )}
        </div>
      ))}
      {!disabled && (
        <Button size="sm" variant="outline" data-testid={`${testIdPrefix}-add`} onClick={() => onChange([...phases, newPhase(phases.length, phases[phases.length - 1])])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add phase
        </Button>
      )}
      {phases.length > 0 && (
        <p className="text-[10px] text-gray-500 inline-flex items-center gap-1">
          <CalendarRange className="w-3 h-3" /> Cycle start and end dates follow the first and last phase automatically.
        </p>
      )}
    </div>
  );
};

export default PhasesEditor;
